/**
 * Utility functions for currency formatting
 */

/**
 * Format amount in Indian Rupees (₹)
 * @param {number} amount - The amount to format
 * @param {boolean} showSymbol - Whether to show the ₹ symbol (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showSymbol = true) => {
  const numAmount = parseFloat(amount) || 0;
  
  // Format with Indian number system (lakhs, crores)
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);

  // If showSymbol is false, remove the ₹ symbol
  return showSymbol ? formatted : formatted.replace('₹', '').trim();
};

/**
 * Format amount without currency symbol
 * @param {number} amount - The amount to format
 * @returns {string} Formatted number string
 */
export const formatAmount = (amount) => {
  return formatCurrency(amount, false);
};

/**
 * Format amount with rupee symbol
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string with ₹ symbol
 */
export const formatRupees = (amount) => {
  return formatCurrency(amount, true);
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number} Parsed amount
 */
export const parseCurrency = (currencyString) => {
  if (typeof currencyString === 'number') return currencyString;
  
  // Remove currency symbols and commas, then parse
  const cleaned = currencyString.toString()
    .replace(/[₹$,\s]/g, '')
    .replace(/[^\d.-]/g, '');
  
  return parseFloat(cleaned) || 0;
};

export default {
  formatCurrency,
  formatAmount,
  formatRupees,
  parseCurrency
};
